import {
	AlertBar,
	Dialog,
	Form,
	FormattedText,
	Tabs,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./HubSidebarManagementDialog.module.less";
import { IFormHandler, useFormHandler } from "client/Forms";
import { contains, normalizeForSearch } from "client/Data/searching";
import { EMPTY_ARRAY } from "client/Misc/utils";
import { getClientId, setClientId } from "client/Misc/clientId";
import {
	HubItemChildNodeFormModel,
	FormModel,
	HubItemGroupNodeFormModel
} from "./common";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { hubDataSourceToLocalizationKeyMap, HubItemNode } from "./HubItemNode";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { never } from "client_legacy/ndaw/errors";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { SearchAndSort, sortKindToNameMap } from "./SearchAndSort";
import {
	HubHierarchySaveModel,
	saveHubSidebar,
	RootHubSideBarNodeViewModel,
	HubItemHubSideBarNodeViewModel,
	CommunityHubSidebarViewModel,
	getHubSidebarManagement,
	HubHierarchySortKind,
	HubItemSidebarNodeSaveModel
} from "client/Api/Hub/hubSidebarManagement";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import { HubDataSource } from "client/Api/Hub/hub";
import { CommunitiesChange, CommunitiesNode } from "./CommunitiesNode";
import { HubEvent, publish } from "../events";
import { RootItemsOrderTabContent } from "./RootItemsOrderTabContent";

export type HubSidebarManagementDialogProps = {
	onSaved(): void;
	onClose(): void;
};

export const HubSidebarManagementDialog = (props: HubSidebarManagementDialogProps): ReactElement => {
	const {
		onSaved,
		onClose: onCloseFromProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({ value: "", version: newGuid() });
	const [sortKind, setSortKind] = useState<HubHierarchySortKind>(HubHierarchySortKind.Default);
	const [formData, setFormData] = useState<FormModel>(initialFormData);
	const [isInfoDismissed, setIsInfoDismissed] = useState<boolean>(false);
	const [nodeClientIdToScrollTo, setNodeClientIdtoScrollToryIdToScroll] = useState<Guid | null>(null);
	const [dialogsWithBackdropRegistrations, setDialogsWithBackdropRegistrations] = useState<readonly Guid[]>([]);
	const [canUserReorderRootNodesAndChangeSortKind, setCanUserReorderRootNodesAndChangeSortKind] = useState<boolean>(false);
	const [activeTabId, setActiveTabId] = useState<TabId>("item-order");

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const haveExternalPersistedChangesBeenMade: RefObject<boolean> = useRef<boolean>(false);
	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	const formHandler: IFormHandler<FormModel> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const normalizedSearchTerm: string = useMemo(
		() => normalizeForSearch(searchTerm.value),
		[searchTerm]
	);

	const submitHandler = useCallback(
		async(): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			const data: HubHierarchySaveModel = prepareForSaving(
				formData,
				canUserReorderRootNodesAndChangeSortKind,
				sortKind
			);

			try {
				await saveHubSidebar(data, globalCancellation);
				alertManager.showSuccess(localize("_ChangesSaved"));
				publish(HubEvent.HierarchyChanged, undefined);
				onSaved();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation
						.tryProcessServerError(e)
				) {
					console.error(e);
					alertManager.showError(localize("_ChangesNotSaved"));
				}

				setIsBusy(false);
			}

		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onSaved,
			sortKind,
			canUserReorderRootNodesAndChangeSortKind
		]
	);

	const doesHubItemChildMatchSearchTerm = useCallback(
		(item: HubItemChildNodeFormModel): boolean => {
			if (normalizedSearchTerm === "") {
				return true;
			}

			const doesNameContainSearchTerm: boolean = contains(
				item.name,
				normalizedSearchTerm
			);

			if (doesNameContainSearchTerm) {
				return true;
			}

			if (item.kind === HubItemCategoryNodeKind.Group) {
				const doesAnyChildMatchSearchTerm: boolean = item
					.groupData
					.children
					.some(doesHubItemChildMatchSearchTerm);

				if (doesAnyChildMatchSearchTerm) {
					return true;
				}
			}

			return false;
		},
		[normalizedSearchTerm]
	);

	const doesCommunityMatchSearchTerm = useCallback(
		(community: CommunityHubSidebarViewModel): boolean => {
			if (normalizedSearchTerm === "") {
				return true;
			}

			return contains(
				community.name,
				normalizedSearchTerm
			);
		},
		[normalizedSearchTerm]
	);

	const changeSortHandler = useCallback(
		(sortKind: HubHierarchySortKind): void => {
			switch (sortKind) {
				case HubHierarchySortKind.Default:
					formHandler
						.reducer
						.setValue(sortByDefault(formHandler.value));

					formHandler
						.validation
						.triggerInputFinished();
					break;
				case HubHierarchySortKind.Custom:
					// Don't change the order
					break;
				default:
					never(sortKind);
			}

			setSortKind(sortKind);
		},
		[formHandler]
	);

	const itemReorderHandler = useCallback(
		(): void => {
			if (!canUserReorderRootNodesAndChangeSortKind) {
				return;
			}

			const from: HubHierarchySortKind = sortKind;
			const to: HubHierarchySortKind = HubHierarchySortKind.Custom;

			if (to === from) {
				return;
			}

			changeSortHandler(to);

			alertManager.showInfo(
				<FormattedText
					text={localize("_SortingChangedFromNameToName")}
					thingsToReplace={[
						{ what: "{{From}}", filler: <b>{sortKindToNameMap[from]}</b> },
						{ what: "{{To}}", filler: <b>{sortKindToNameMap[to]}</b> }
					]}
				/>
			);
		},
		[
			alertManager,
			canUserReorderRootNodesAndChangeSortKind,
			changeSortHandler,
			sortKind
		]
	);

	const externalChangesPersistedHandler = useCallback(
		(): void => {
			haveExternalPersistedChangesBeenMade.current = true;
		},
		[]
	);

	const communitiesChangedHandler = useCallback(
		(change: CommunitiesChange): void => {
			externalChangesPersistedHandler();

			if (change.kind === "added") {
				setSearchTerm({ value: "", version: newGuid() });
				setNodeClientIdtoScrollToryIdToScroll(change.clientId);
			}
		},
		[externalChangesPersistedHandler]
	);

	const closeHandler = useCallback(
		(): void => {
			if (haveExternalPersistedChangesBeenMade.current) {
				publish(HubEvent.HierarchyChanged, undefined);
			}

			onClose();
		},
		[onClose]
	);

	const newHubItemNodeAddedHandler = useCallback(
		(node: HubItemChildNodeFormModel): void => {
			setNodeClientIdtoScrollToryIdToScroll(getClientId(node));
			setSearchTerm({ value: "", version: newGuid() });
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			async function fetchData(signal: AbortSignal) {
				try {
					setIsBusy(true);
					const {
						canUserReorderRootNodesAndChangeSortKind,
						hierarchy
					} = await getHubSidebarManagement(signal);

					const {
						shouldUseDefaultSorting,
						nodes
					} = hierarchy;

					setCanUserReorderRootNodesAndChangeSortKind(canUserReorderRootNodesAndChangeSortKind);
					const formData: FormModel = convertToFormModel(nodes);
					setFormData(formData);
					setSortKind(shouldUseDefaultSorting
						? HubHierarchySortKind.Default
						: HubHierarchySortKind.Custom
					);

					setActiveTabId(getFirstAvailableTab(formData, canUserReorderRootNodesAndChangeSortKind));
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					closeHandler();
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[alertManager, closeHandler]
	);

	const registerDialogWithBackdropHandler = useCallback(
		(): (() => void) => {
			const id: Guid = newGuid();
			setDialogsWithBackdropRegistrations((x) => ([...x, id]));

			return (): void => {
				setDialogsWithBackdropRegistrations(
					(x) => x.filter((x) => x !== id)
				);
			};
		},
		[]
	);

	const isSearching: boolean = Boolean(normalizedSearchTerm);

	const canReorderItems: boolean = !isSearching
		&& (
			sortKind === HubHierarchySortKind.Custom
			|| canUserReorderRootNodesAndChangeSortKind
		);

	return (
		<Dialog
			trackingId="hub-sidebar-management"
			variant="left-slider"
			onClose={closeHandler}
			hasBusyIndicator={isBusy}
			backdrop={
				dialogsWithBackdropRegistrations.length > 0
					? "invisible"
					: undefined
			}
		>
			<Dialog.Header
				title={localize("_ManageHubSidebar")}
				onClose={closeHandler}
			/>
			<Dialog.Body>
				{!isInfoDismissed && (
					<div className={styles.info}>
						<AlertBar
							kind="info"
							trackingId="default-sorting-info"
							onDismiss={() => setIsInfoDismissed(true)}
							isInline
						>
							{localize("_ManageNavigationSidebarInfo")}
						</AlertBar>
					</div>
				)}
				<SearchAndSort
					searchTerm={searchTerm}
					onSearch={setSearchTerm}
					sortMode={
						canUserReorderRootNodesAndChangeSortKind
							? {
								kind: "enabled",
								sortKind,
								onSortKindChange: changeSortHandler
							}
							: { kind: "disabled" }
					}
				/>
				<Form onSubmit="NONE">
					<Tabs<TabId>
						activeTabId={activeTabId}
						onActiveTabIdChange={setActiveTabId}
					>
						{canUserReorderRootNodesAndChangeSortKind && (
							<Tabs.Tab<TabId>
								id="item-order"
								title={localize("_ItemOrder")}
							>
								<RootItemsOrderTabContent
									attachable={formHandler.for("sourcesInOrder")}
									normalizedSearchTerm={normalizedSearchTerm}
									canReorderItems={canReorderItems}
									onItemReorder={itemReorderHandler}
								/>
							</Tabs.Tab>
						)}
						{formHandler
							.for("announcementsRoot")
							.whenType(
								x => x.isSet,
								announcementsHandler => (
									<Tabs.Tab<TabId>
										id="announcements"
										title={localize(hubDataSourceToLocalizationKeyMap[HubDataSource.Announcement])}
									>
										<HubItemNode
											attachable={announcementsHandler.for("value")}
											doesMatchSearchTerm={doesHubItemChildMatchSearchTerm}
											canReorderItems={canReorderItems}
											nodeClientIdToScrollTo={nodeClientIdToScrollTo}
											onNodeClientIdToScrollToChange={setNodeClientIdtoScrollToryIdToScroll}
											registerDialogWithBackdrop={registerDialogWithBackdropHandler}
											onNodeChanged={externalChangesPersistedHandler}
											onNewNodeAdded={newHubItemNodeAddedHandler}
											onItemReorder={itemReorderHandler}
										/>
									</Tabs.Tab>
								)
							)
						}
						{formHandler
							.for("communitiesRoot")
							.whenType(
								x => x.isSet,
								communitiesHandler => (
									<Tabs.Tab<TabId>
										id="communities"
										title={localize(hubDataSourceToLocalizationKeyMap[HubDataSource.Community])}
									>
										<CommunitiesNode
											attachable={communitiesHandler.for("value")}
											doesMatchSearchTerm={doesCommunityMatchSearchTerm}
											registerDialogWithBackdrop={registerDialogWithBackdropHandler}
											onCommunitiesChanged={communitiesChangedHandler}
											nodeClientIdToScrollTo={nodeClientIdToScrollTo}
											onNodeClientIdToScrollToChange={setNodeClientIdtoScrollToryIdToScroll}
										/>
									</Tabs.Tab>
								)
							)
						}
						{formHandler
							.for("eventsRoot")
							.whenType(
								x => x.isSet,
								eventsHandler => (
									<Tabs.Tab<TabId>
										id="events"
										title={localize(hubDataSourceToLocalizationKeyMap[HubDataSource.Event])}
									>
										<HubItemNode
											attachable={eventsHandler.for("value")}
											doesMatchSearchTerm={doesHubItemChildMatchSearchTerm}
											canReorderItems={canReorderItems}
											nodeClientIdToScrollTo={nodeClientIdToScrollTo}
											onNodeClientIdToScrollToChange={setNodeClientIdtoScrollToryIdToScroll}
											registerDialogWithBackdrop={registerDialogWithBackdropHandler}
											onNodeChanged={externalChangesPersistedHandler}
											onNewNodeAdded={newHubItemNodeAddedHandler}
											onItemReorder={itemReorderHandler}
										/>
									</Tabs.Tab>
								)
							)
						}
						{formHandler
							.for("knowledgeItemsRoot")
							.whenType(
								x => x.isSet,
								knowledgeItemsHandler => (
									<Tabs.Tab<TabId>
										id="knowledge-items"
										title={localize(hubDataSourceToLocalizationKeyMap[HubDataSource.KnowledgeItem])}
									>
										<HubItemNode
											attachable={knowledgeItemsHandler.for("value")}
											doesMatchSearchTerm={doesHubItemChildMatchSearchTerm}
											canReorderItems={canReorderItems}
											nodeClientIdToScrollTo={nodeClientIdToScrollTo}
											onNodeClientIdToScrollToChange={setNodeClientIdtoScrollToryIdToScroll}
											registerDialogWithBackdrop={registerDialogWithBackdropHandler}
											onNodeChanged={externalChangesPersistedHandler}
											onNewNodeAdded={newHubItemNodeAddedHandler}
											onItemReorder={itemReorderHandler}
										/>
									</Tabs.Tab>
								)
							)
						}
					</Tabs>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={submitHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						trackingId="cancel"
						onClick={closeHandler}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
HubSidebarManagementDialog.displayName = "HubSidebarManagementDialog";

type TabId =
	| "item-order"
	| "announcements"
	| "communities"
	| "events"
	| "knowledge-items";

const sourceDefaultSortOrder: readonly HubDataSource[] = [
	HubDataSource.Announcement,
	HubDataSource.Community,
	HubDataSource.Event,
	HubDataSource.KnowledgeItem
];

function sortByDefault(
	model: FormModel
): FormModel {
	const {
		sourcesInOrder,
		announcementsRoot,
		eventsRoot,
		knowledgeItemsRoot
	} = model;

	return {
		...model,
		sourcesInOrder: sortSourcesByDefault(sourcesInOrder),
		announcementsRoot: announcementsRoot.isSet
			? {
				isSet: true,
				value: {
					...announcementsRoot.value,
					children: sortChildrenByDefault(
						announcementsRoot
							.value
							.children
					)
				}
			}
			: { isSet: false },
		eventsRoot: eventsRoot.isSet
			? {
				isSet: true,
				value: {
					...eventsRoot.value,
					children: sortChildrenByDefault(
						eventsRoot
							.value
							.children
					)
				}
			}
			: { isSet: false },
		knowledgeItemsRoot: knowledgeItemsRoot.isSet
			? {
				isSet: true,
				value: {
					...knowledgeItemsRoot.value,
					children: sortChildrenByDefault(
						knowledgeItemsRoot
							.value
							.children
					)
				}
			}
			: { isSet: false }
	};
}

function sortSourcesByDefault(
	sources: readonly HubDataSource[]
): readonly HubDataSource[] {
	return sourceDefaultSortOrder.filter(x => sources.includes(x));
}

function sortChildrenByDefault(
	children: readonly HubItemChildNodeFormModel[]
): readonly HubItemChildNodeFormModel[] {
	return sortBy(
		children,
		(item) => item.name
			.toLowerCase()
	)
		.map(
			x => {
				if (x.kind === HubItemCategoryNodeKind.Group) {
					return {
						...x,
						groupData: {
							...x.groupData,
							children: sortChildrenByDefault(x.groupData.children)
						}
					};
				}

				return x;
			}
		);
}

function convertToFormModel(
	nodes: readonly RootHubSideBarNodeViewModel[]
): FormModel {
	const result: FormModel = {...initialFormData};
	const sourcesInOrder: HubDataSource[] = [];

	for (const node of nodes) {
		sourcesInOrder.push(node.source);

		switch (node.source) {
			case HubDataSource.Announcement:
				result.announcementsRoot = {
					isSet: true,
					value: setClientId({
						source: HubDataSource.Announcement,
						children: node
							.children
							.map(convertHubItemNodeToFormModel)
					})
				};
				break;
			case HubDataSource.Community:
				result.communitiesRoot = {
					isSet: true,
					value: setClientId({
						source: HubDataSource.Community,
						children: node
							.children
							.map(setClientId)
					})
				};
				break;
			case HubDataSource.Event:
				result.eventsRoot = {
					isSet: true,
					value: setClientId({
						source: HubDataSource.Event,
						children: node
							.children
							.map(convertHubItemNodeToFormModel)
					})
				};
				break;
			case HubDataSource.KnowledgeItem:
				result.knowledgeItemsRoot = {
					isSet: true,
					value: setClientId({
						source: HubDataSource.KnowledgeItem,
						children: node
							.children
							.map(convertHubItemNodeToFormModel)
					})
				};
				break;
			default:
				never(node);
		}
	}

	result.sourcesInOrder = sourcesInOrder;

	return result;
}

function convertHubItemNodeToFormModel(
	item: HubItemHubSideBarNodeViewModel
): HubItemChildNodeFormModel {
	switch (item.kind) {
		case HubItemCategoryNodeKind.Category:
			return setClientId({
				kind: HubItemCategoryNodeKind.Category,
				name: item.name,
				categoryData: {
					categoryId: item.categoryId,
				}
			});
		case HubItemCategoryNodeKind.Group:
			return setClientId<Omit<HubItemGroupNodeFormModel, "__hasClientId">>({
				kind: HubItemCategoryNodeKind.Group,
				name: item.name,
				groupData: {
					groupId: item.groupId,
					children: item.children
						.map(convertHubItemNodeToFormModel)
				}
			});
		default:
			never(item);
	}
}

function prepareForSaving(
	formData: FormModel,
	canUserReorderRootNodesAndChangeSortKind: boolean,
	sortKind: HubHierarchySortKind
): HubHierarchySaveModel {
	const {
		announcementsRoot,
		communitiesRoot,
		eventsRoot,
		knowledgeItemsRoot,
		sourcesInOrder
	} = formData;

	return {
		sorting: canUserReorderRootNodesAndChangeSortKind
			? {
				isSet: true,
				value: {
					kind: sortKind,
					sourcesInOrder: sortKind === HubHierarchySortKind.Custom
						? sourcesInOrder
						: null
				}
			}
			: { isSet: false },
		announcementsRoot: announcementsRoot.isSet
			? {
				isSet: true,
				value: {
					source: HubDataSource.Announcement,
					children: announcementsRoot
						.value
						.children.map(convertHubItemToSaveModel)
				}
			}
			: { isSet: false },
		communitiesRoot: communitiesRoot.isSet
			? {
				isSet: true,
				value: { source: HubDataSource.Community }
			}
			: { isSet: false },
		eventsRoot: eventsRoot.isSet
			? {
				isSet: true,
				value: {
					source: HubDataSource.Event,
					children: eventsRoot
						.value
						.children.map(convertHubItemToSaveModel)
				}
			}
			: { isSet: false },
		knowledgeItemsRoot: knowledgeItemsRoot.isSet
			? {
				isSet: true,
				value: {
					source: HubDataSource.KnowledgeItem,
					children: knowledgeItemsRoot
						.value
						.children.map(convertHubItemToSaveModel)
				}
			}
			: { isSet: false },
	};
}

function convertHubItemToSaveModel(
	item: HubItemChildNodeFormModel
): HubItemSidebarNodeSaveModel {
	switch (item.kind) {
		case HubItemCategoryNodeKind.Category:
			return {
				kind: HubItemCategoryNodeKind.Category,
				categoryData: item.categoryData
			};
		case HubItemCategoryNodeKind.Group:
			return {
				kind: HubItemCategoryNodeKind.Group,
				groupData: {
					...item.groupData,
					children: item.groupData
						.children
						.map(convertHubItemToSaveModel)
				}
			};
		default:
			never(item);
	}
}

const initialFormData: FormModel = {
	sourcesInOrder: EMPTY_ARRAY,
	announcementsRoot: { isSet: false },
	communitiesRoot: { isSet: false },
	eventsRoot: { isSet: false },
	knowledgeItemsRoot: { isSet: false }
};

function getFirstAvailableTab(
	model: FormModel,
	canUserReorderRootNodesAndChangeSortKind: boolean
): TabId {
	const {
		announcementsRoot,
		communitiesRoot,
		eventsRoot,
		knowledgeItemsRoot
	} = model;

	if (canUserReorderRootNodesAndChangeSortKind) {
		return "item-order";
	}

	if (announcementsRoot.isSet) {
		return "announcements";
	}

	if (communitiesRoot.isSet) {
		return "communities";
	}

	if (eventsRoot.isSet) {
		return "events";
	}

	if (knowledgeItemsRoot.isSet) {
		return "knowledge-items";
	}

	throw new Error("No hub item is available to manage");
}

export type HubSidebarManagementDialogInitialData = Omit<
	HubSidebarManagementDialogProps,
	"onClose" | "onSaved"
>;

export function useHubSidebarManagementDialog(): [
	element: ReactElement | null,
	open: (props: HubSidebarManagementDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSaved) => (
			<HubSidebarManagementDialog
				{...props}
				onClose={onClose}
				onSaved={onSaved}
			/>
		)
	);
}
